/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Aui 컴포넌트 내부의 구성요소 클래스를 정의한다.
 *
 * @file /src/elements/title/Control.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2025. 12. 18.
 */
import * as parent from '@base/Element';
import { Component } from '@base/Component';
import { Bar as Title } from '@elements/title/Bar';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 아이콘
     */
    icon: string;

    /**
     * 핸들러
     */
    handler: (me: Control) => Promise<void>;
}

export class Control extends parent.Element {
    /**
     * 엘리먼트 타입
     */
    type: string = 'control';

    /**
     * 부모가 되는 타이틀바
     */
    title: Title;

    /**
     * 아이콘
     */
    icon: string;

    /**
     * 핸들러
     */
    handler: (control: Control) => Promise<void>;

    /**
     * 버튼 DOM
     */
    $button: Dom = null;

    /**
     * 아이콘 DOM
     */
    $icon: Dom = null;

    /**
     * 제목 엘리먼트를 정의한다.
     *
     * @param properties - 엘리먼트 초기설정
     */
    constructor(public properties: Properties<Control>, title: Title) {
        super(properties);

        this.icon = this.properties.icon ?? undefined;
        this.title = title;
        this.handler = this.properties.handler;
    }

    /**
     * 타이틀바를 가져온다.
     *
     * @returns 타이틀바
     */
    getTitle(): Title {
        return this.title;
    }

    /**
     * 부모컴포넌트를 타이틀바를 통해 가져온다.
     *
     * @returns 부모 컴포넌트
     */
    getComponent(): Component | null {
        return this.title.getComponent();
    }

    /**
     * 엘리먼트 DOM 을 가져온다.
     *
     * @returns 엘리먼트 DOM
     */
    $getElement(): Dom {
        this.$element ??= Html.create('li');

        return this.$element;
    }

    /**
     * 버튼 DOM 을 가져온다.
     *
     * @returns 버튼 DOM
     */
    $getButton(): Dom {
        if (this.$button === null) {
            this.$button = Html.create('button');
            this.$button.setAttr('type', 'button');
            this.$button.on('click', (_, e: PointerEvent) => {
                this.handler(this);
                e.stopImmediatePropagation();
            });
        }

        return this.$button;
    }

    /**
     * 아이콘 DOM 을 가져온다.
     *
     * @returns 아이콘 DOM
     */
    $getIcon(): Dom {
        if (this.$icon === null) {
            this.$icon = Html.create('i');
            this.$icon.addClass(...this.icon.split(' '));
        }

        return this.$icon;
    }

    /**
     * 아이콘을 가져온다.
     *
     * @returns 아이콘
     */
    getIcon(): string {
        return this.icon;
    }

    /**
     * 아이콘을 변경한다.
     *
     * @param icon - 변경할 아이콘 (NULL 인 경우 아이콘을 제거한다.)
     */
    setIcon(icon: string | null = null): void {
        this.icon = icon === null ? undefined : icon;

        const $icon = this.$getIcon();
        $icon.removeClass();

        if (this.icon !== null) {
            $icon.addClass(...this.icon.split(' '));
        }
    }

    /**
     * 엘리먼트를 렌더링한다.
     */
    async render(): Promise<void> {
        if (this.isRendered() == true) {
            return;
        }

        if (this.isHidden() == true) {
            this.hide();
        }

        this.$getButton().append(this.$getIcon());
        this.$getElement().append(this.$getButton());

        await this.onRender();
    }

    /**
     * 엘리먼트를 제거한다.
     *
     * @param removeDom - 엘리먼트 DOM 을 제거할지 여부
     */
    async remove(removeDom: boolean = true): Promise<void> {
        if (removeDom == true) {
            this.title.removeControl(this, false);
        }

        this.$button = null;
        this.$icon = null;

        await super.remove();
    }
}
